// ══════════════════════════════════════════════════════════════════════════════
// RÉSEAU SOCIAL PRO - Feed réservé aux professionnels
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart, MessageCircle, Share2, Bookmark, MoreHorizontal, Image,
  Send, Smile, ThumbsUp, Users, Star, MapPin, Calendar, TrendingUp,
  PlusCircle, Filter, RefreshCw, BadgeCheck, Building2, Wrench, Sparkles,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { QuickResponseButton } from '../../components/ai/QuickResponseButton';
import ResponseAssistant from '../../components/ai/ResponseAssistant';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Types de posts
const POST_TYPES = [
  { id: 'all', label: 'Tout' },
  { id: 'realisation', label: 'Réalisations' },
  { id: 'conseil', label: 'Conseils' },
  { id: 'question', label: 'Questions' },
  { id: 'opportunite', label: 'Opportunités' },
];

// Données de démo
const DEMO_POSTS = [
  {
    id: 1,
    author: { name: 'Martin Plomberie', metier: 'Plombier', avatar: 'MP', verified: true, rating: 4.9 },
    content: 'Nouvelle réalisation : rénovation complète d\'une salle de bain dans une maison des années 70. Pose d\'un receveur extra-plat, colonne de douche thermostatique et meuble double vasque. Le client est ravi ! 💪',
    images: ['https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=600'],
    type: 'realisation',
    likes: 47,
    comments: 12,
    shares: 3,
    time: 'Il y a 2h',
    isLiked: false,
    isSaved: false,
    commentsList: [
      { id: 'c1', author: 'Électricité Dupont', avatar: 'ED', text: 'Superbe travail ! Tu as utilisé quoi comme marque pour le mitigeur ?', time: 'Il y a 1h' },
      { id: 'c2', author: 'Client Satisfait', avatar: 'CS', text: 'Vous intervenez sur quelle zone ? J\'ai un projet similaire sur Lyon.', time: 'Il y a 45min' },
    ]
  },
  {
    id: 2,
    author: { name: 'Électricité Dupont', metier: 'Électricien', avatar: 'ED', verified: true, rating: 4.8 },
    content: '⚡ Astuce du jour : Pour un tableau électrique propre, utilisez toujours des peignes de raccordement plutôt que des fils volants. C\'est plus esthétique ET plus sécurisé ! Qui d\'autre fait ça systématiquement ?',
    images: [],
    type: 'conseil',
    likes: 89,
    comments: 34,
    shares: 15,
    time: 'Il y a 5h',
    isLiked: true,
    isSaved: false,
    commentsList: [
      { id: 'c3', author: 'Pro Bâtiment', avatar: 'PB', text: 'Totalement d\'accord ! C\'est la base. Et ça facilite les dépannages futurs.', time: 'Il y a 4h' },
    ]
  },
  {
    id: 3,
    author: { name: 'Pro Bâtiment', metier: 'Maçon', avatar: 'PB', verified: false, rating: 4.6 },
    content: '🤔 Question aux pros : On m\'a proposé un chantier de rénovation énergétique avec ITE sur une maison en pierre. Avez-vous des retours d\'expérience sur ce type de projet ? Des pièges à éviter ?',
    images: [],
    type: 'question',
    likes: 23,
    comments: 45,
    shares: 2,
    time: 'Il y a 8h',
    isLiked: false,
    isSaved: true,
    commentsList: [
      { id: 'c4', author: 'Isolation Expert', avatar: 'IE', text: 'Attention à la gestion de l\'humidité ! Sur une maison en pierre, l\'ITE peut bloquer la respiration des murs. Je te conseille de faire un diagnostic préalable.', time: 'Il y a 6h' },
      { id: 'c5', author: 'Marie Architecte', avatar: 'MA', text: 'Avez-vous envisagé l\'isolation par l\'intérieur à la place ? Parfois plus adapté aux vieilles bâtisses.', time: 'Il y a 5h' },
    ]
  },
  {
    id: 4,
    author: { name: 'Carrelage Elite', metier: 'Carreleur', avatar: 'CE', verified: true, rating: 4.9 },
    content: '🚨 RECHERCHE SOUS-TRAITANT 🚨\n\nGros chantier sur Nice - 450m² de pose carrelage grand format 120x120 dans un hôtel. Besoin d\'un carreleur expérimenté pour les 3 prochaines semaines. Rémunération attractive. MP si intéressé !',
    images: [],
    type: 'opportunite',
    likes: 12,
    comments: 8,
    shares: 22,
    time: 'Hier',
    isLiked: false,
    isSaved: false,
    commentsList: []
  },
  {
    id: 5,
    author: { name: 'Peinture & Déco Pro', metier: 'Peintre', avatar: 'PD', verified: true, rating: 4.7 },
    content: 'Avant / Après sur un appartement haussmannien 🎨\n\nLe propriétaire voulait garder le cachet tout en modernisant. On a opté pour un blanc cassé RAL 9010 en finition velours sur les murs et un bleu profond RAL 5011 pour mettre en valeur les moulures.\n\nRésultat : élégance intemporelle !',
    images: [
      'https://images.unsplash.com/photo-1513694203232-719a280e022f?w=600',
      'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=600'
    ],
    type: 'realisation',
    likes: 156,
    comments: 28,
    shares: 19,
    time: 'Hier',
    isLiked: true,
    isSaved: true,
    commentsList: [
      { id: 'c6', author: 'Décorateur Pro', avatar: 'DP', text: 'Magnifique ! Le choix des couleurs est parfait. Tu pourrais partager la référence exacte du bleu ?', time: 'Il y a 20h' },
      { id: 'c7', author: 'Client Potentiel', avatar: 'CP', text: 'Je cherche exactement ce type de prestation pour mon appartement. Vous intervenez sur Paris 15ème ?', time: 'Il y a 18h' },
    ]
  },
];

// Composant Post
function PostCard({ post, onLike, onSave }) {
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
      data-testid={`post-card-${post.id}`}
    >
      {/* Header */}
      <div className="p-4 flex items-start gap-3">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#1E3A5F] to-[#2d5a8a] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
          {post.author.avatar}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-900">{post.author.name}</span>
            {post.author.verified && (
              <BadgeCheck size={16} className="text-[#1E3A5F]" />
            )}
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>{post.author.metier}</span>
            <span>•</span>
            <div className="flex items-center gap-1">
              <Star size={12} className="text-amber-500 fill-amber-500" />
              <span>{post.author.rating}</span>
            </div>
            <span>•</span>
            <span>{post.time}</span>
          </div>
        </div>
        <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
          <MoreHorizontal size={20} className="text-gray-400" />
        </button>
      </div>

      {/* Content */}
      <div className="px-4 pb-3">
        <p className="text-gray-800 whitespace-pre-line">{post.content}</p>
      </div>

      {/* Images */}
      {post.images && post.images.length > 0 && (
        <div className={`${post.images.length > 1 ? 'grid grid-cols-2 gap-1' : ''}`}>
          {post.images.map((img, index) => (
            <img
              key={index}
              src={img}
              alt=""
              className="w-full h-64 object-cover"
            />
          ))}
        </div>
      )}

      {/* Type badge */}
      <div className="px-4 py-2">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
          post.type === 'realisation' ? 'bg-green-100 text-green-700' :
          post.type === 'conseil' ? 'bg-blue-100 text-blue-700' :
          post.type === 'question' ? 'bg-purple-100 text-purple-700' :
          post.type === 'opportunite' ? 'bg-orange-100 text-orange-700' :
          'bg-gray-100 text-gray-700'
        }`}>
          {post.type === 'realisation' ? '✅ Réalisation' :
           post.type === 'conseil' ? '💡 Conseil' :
           post.type === 'question' ? '❓ Question' :
           post.type === 'opportunite' ? '🚨 Opportunité' : post.type}
        </span>
      </div>

      {/* Stats */}
      <div className="px-4 py-2 flex items-center justify-between text-sm text-gray-500 border-t border-gray-100">
        <div className="flex items-center gap-1">
          <ThumbsUp size={14} className="text-[#1E3A5F]" />
          <span>{post.likes}</span>
        </div>
        <div className="flex items-center gap-4">
          <span>{post.comments} commentaires</span>
          <span>{post.shares} partages</span>
        </div>
      </div>

      {/* Actions */}
      <div className="px-4 py-2 flex items-center justify-between border-t border-gray-100">
        <button
          onClick={() => onLike(post.id)}
          data-testid={`like-btn-${post.id}`}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            post.isLiked ? 'text-[#F97316] bg-orange-50' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Heart size={20} className={post.isLiked ? 'fill-current' : ''} />
          <span className="text-sm font-medium">J'aime</span>
        </button>
        <button
          onClick={() => setShowComments(!showComments)}
          data-testid={`comment-toggle-${post.id}`}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <MessageCircle size={20} />
          <span className="text-sm font-medium">Commenter</span>
        </button>
        <button className="flex items-center gap-2 px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors">
          <Share2 size={20} />
          <span className="text-sm font-medium">Partager</span>
        </button>
        <button
          onClick={() => onSave(post.id)}
          data-testid={`save-btn-${post.id}`}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            post.isSaved ? 'text-[#1E3A5F] bg-blue-50' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Bookmark size={20} className={post.isSaved ? 'fill-current' : ''} />
        </button>
      </div>

      {/* Comments section */}
      <AnimatePresence>
        {showComments && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-gray-100 overflow-hidden"
            data-testid={`comments-section-${post.id}`}
          >
            <div className="p-4 space-y-4">
              {/* Commentaires existants */}
              {post.commentsList && post.commentsList.length > 0 && (
                <div className="space-y-3">
                  {post.commentsList.map((comment) => (
                    <div key={comment.id} className="flex gap-3 group" data-testid={`comment-${comment.id}`}>
                      {/* Avatar commentaire */}
                      <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-xs font-bold text-gray-600 flex-shrink-0">
                        {comment.avatar}
                      </div>
                      
                      {/* Contenu commentaire */}
                      <div className="flex-1 min-w-0">
                        <div className="bg-gray-100 rounded-2xl px-4 py-2">
                          <p className="font-semibold text-sm text-gray-900">{comment.author}</p>
                          <p className="text-sm text-gray-700">{comment.text}</p>
                        </div>
                        
                        {/* Actions commentaire */}
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                          <span>{comment.time}</span>
                          <button className="hover:text-[#1E3A5F] font-medium">
                            J'aime
                          </button>
                          <button className="hover:text-[#1E3A5F] font-medium">
                            Répondre
                          </button>
                          
                          {/* Bouton Aide à répondre - Toujours visible */}
                          <QuickResponseButton 
                            message={comment.text} 
                            context="social" 
                            variant="pill"
                            size="sm"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Zone de saisie nouveau commentaire */}
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#1E3A5F] flex items-center justify-center text-white text-xs font-bold">
                  JC
                </div>
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Écrire un commentaire..."
                    className="w-full px-4 py-2 pr-20 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
                    data-testid={`comment-input-${post.id}`}
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                    <button className="p-1.5 hover:bg-gray-200 rounded-full">
                      <Smile size={16} className="text-gray-400" />
                    </button>
                    <button className="p-1.5 hover:bg-gray-200 rounded-full">
                      <Image size={16} className="text-gray-400" />
                    </button>
                    <button className="p-1.5 bg-[#F97316] hover:bg-[#ea6c0a] rounded-full" data-testid={`send-comment-${post.id}`}>
                      <Send size={14} className="text-white" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// Page principale
export default function ReseauSocialProPage() {
  const [posts, setPosts] = useState(DEMO_POSTS);
  const [activeFilter, setActiveFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  
  // État pour le scroll des filtres mobile
  const [showScrollHint, setShowScrollHint] = useState(true);
  const filterScrollRef = useRef(null);
  
  const handleFilterScroll = () => {
    if (filterScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = filterScrollRef.current;
      setShowScrollHint(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  // Filtrer les posts
  const filteredPosts = activeFilter === 'all' 
    ? posts 
    : posts.filter(p => p.type === activeFilter);

  // Like un post
  const handleLike = (postId) => {
    setPosts(prev => prev.map(post => 
      post.id === postId 
        ? { ...post, isLiked: !post.isLiked, likes: post.isLiked ? post.likes - 1 : post.likes + 1 }
        : post
    ));
  };

  // Sauvegarder un post
  const handleSave = (postId) => {
    setPosts(prev => prev.map(post => 
      post.id === postId 
        ? { ...post, isSaved: !post.isSaved }
        : post
    ));
    toast.success('Post sauvegardé !');
  };

  // Rafraîchir le feed
  const handleRefresh = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      toast.success('Feed actualisé !');
    }, 1000);
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users size={24} className="text-[#1E3A5F]" />
            Réseau Social
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Échange avec la communauté des pros et des particuliers
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isLoading}
          className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
        >
          <RefreshCw size={20} className={`text-gray-500 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Quick post */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1E3A5F] to-[#2d5a8a] flex items-center justify-center text-white font-bold text-sm">
            JC
          </div>
          <Link
            to="/cockpit-pro"
            onClick={() => {
              // Trigger publish modal
              window.dispatchEvent(new CustomEvent('openPublishModal'));
            }}
            className="flex-1 px-4 py-2.5 bg-gray-100 rounded-full text-gray-500 text-sm hover:bg-gray-200 transition-colors text-left"
          >
            Partager une réalisation, un conseil...
          </Link>
          <button className="p-2 hover:bg-orange-50 rounded-lg transition-colors">
            <Image size={20} className="text-[#F97316]" />
          </button>
        </div>
      </div>

      {/* Filters - Style premium avec scroll horizontal */}
      <div className="mb-6">
        {/* Mobile */}
        <div className="sm:hidden">
          <div className="relative flex items-center">
            <div 
              ref={filterScrollRef}
              onScroll={handleFilterScroll}
              className="flex gap-1.5 overflow-x-auto scrollbar-hide flex-1 pr-6"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {POST_TYPES.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setActiveFilter(type.id)}
                  data-testid={`filter-${type.id}`}
                  className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${
                    activeFilter === type.id
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
            
            {/* Flèche orange discrète */}
            {showScrollHint && (
              <div className="flex-shrink-0 flex items-center pointer-events-none">
                <ChevronRight size={18} className="text-orange-500" strokeWidth={2.5} />
              </div>
            )}
          </div>
        </div>
        
        {/* Desktop */}
        <div className="hidden sm:flex flex-wrap gap-2">
          {POST_TYPES.map((type) => (
            <button
              key={type.id}
              onClick={() => setActiveFilter(type.id)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                activeFilter === type.id
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {type.label}
            </button>
          ))}
        </div>
      </div>

      {/* Posts */}
      <div className="space-y-4">
        {filteredPosts.map((post) => (
          <PostCard
            key={post.id}
            post={post}
            onLike={handleLike}
            onSave={handleSave}
          />
        ))}
      </div>

      {/* Load more */}
      <div className="text-center py-8">
        <button className="px-6 py-2 bg-gray-100 text-gray-600 rounded-full text-sm font-medium hover:bg-gray-200 transition-colors">
          Charger plus de posts
        </button>
      </div>
    </div>
  );
}
